import { z } from "zod";

import { createAttribute } from "@coltorapps/builder";

const componentValidatorSchema = z
  .object({
    code: z.string().optional(),
    errorMessage: z.string().optional(),
    enableApi: z.boolean().optional(),
    isApi: z.boolean().optional(),
  })
  .transform(({ isApi, ...value }) => ({
    ...value,
    enableApi: value.enableApi ?? isApi,
  }));

const registryValidatorSchema = z.object({
  validatorId: z.string(),
  enableApi: z.boolean().optional(),
});

const validatorSchema = z.preprocess(
  (value) => value ?? undefined,
  z
    .object({
      required: z.boolean().optional(),
      componentValidator: componentValidatorSchema.optional(),
      registryValidators: z.array(registryValidatorSchema).optional(),
      validatorId: z.string().optional(),
      code: z.string().optional(),
      errorMessage: z.string().optional(),
      isApi: z.boolean().optional(),
    })
    .default({ required: false })
    .transform(
      ({
        code,
        errorMessage,
        isApi,
        validatorId,
        registryValidators,
        componentValidator,
        ...value
      }) => {
        const nextComponentValidator =
          componentValidator ??
          (code !== undefined ||
          errorMessage !== undefined ||
          isApi !== undefined
            ? {
                code,
                errorMessage,
                enableApi: isApi,
              }
            : undefined);
        const nextRegistryValidators =
          registryValidators ??
          (validatorId ? [{ validatorId }] : undefined);

        return {
          required: value.required ?? false,
          ...(nextComponentValidator
            ? { componentValidator: nextComponentValidator }
            : {}),
          ...(nextRegistryValidators?.length
            ? { registryValidators: nextRegistryValidators }
            : {}),
        };
      },
    ),
);

export type ValidatorAttributeValue = z.infer<typeof validatorSchema>;

export function normalizeValidatorAttribute(value: unknown) {
  return validatorSchema.parse(value);
}

export function getComponentValidator(value: unknown) {
  if (!value || typeof value !== "object") return undefined;

  const validator = value as Record<string, unknown>;
  const componentValidator = validator.componentValidator;
  if (componentValidator && typeof componentValidator === "object") {
    const nested = componentValidator as Record<string, unknown>;
    return {
      code: typeof nested.code === "string" ? nested.code : undefined,
      errorMessage:
        typeof nested.errorMessage === "string"
          ? nested.errorMessage
          : undefined,
      enableApi:
        typeof nested.enableApi === "boolean"
          ? nested.enableApi
          : typeof nested.isApi === "boolean"
            ? nested.isApi
            : undefined,
    };
  }

  return {
    code: typeof validator.code === "string" ? validator.code : undefined,
    errorMessage:
      typeof validator.errorMessage === "string"
        ? validator.errorMessage
        : undefined,
    enableApi:
      typeof validator.isApi === "boolean" ? validator.isApi : undefined,
  };
}

export function getRegistryValidators(value: unknown) {
  if (!value || typeof value !== "object") return [];

  const validator = value as Record<string, unknown>;
  if (Array.isArray(validator.registryValidators)) {
    return validator.registryValidators.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const registryValidator = item as Record<string, unknown>;
      return typeof registryValidator.validatorId === "string"
        ? [
            {
              validatorId: registryValidator.validatorId,
              ...(typeof registryValidator.enableApi === "boolean"
                ? { enableApi: registryValidator.enableApi }
                : {}),
            },
          ]
        : [];
    });
  }

  return typeof validator.validatorId === "string" &&
    validator.validatorId.trim() !== ""
    ? [{ validatorId: validator.validatorId }]
    : [];
}

export function getValidatorRequired(value: unknown) {
  if (!value || typeof value !== "object") return false;
  const required = (value as Record<string, unknown>).required;
  return typeof required === "boolean" ? required : false;
}

export const validatorAttribute = createAttribute({
  name: "validator",
  validate(value) {
    return validatorSchema.parse(value);
  },
});
